import { useMemo, useState, useEffect } from 'react';
import type { ApiProviderProfile } from '../types/matchmaking.types';

type UseProviderImagesProps = {
  preloadedImageUrl?: string | null;
  providerProfile?: ApiProviderProfile;
  isExpanded: boolean;
};

export const useProviderImages = ({
  preloadedImageUrl,
  providerProfile,
  isExpanded,
}: UseProviderImagesProps) => {
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [shouldLoadImage, setShouldLoadImage] = useState(false);
  const [profileImages, setProfileImages] = useState<string[]>([]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isProfileImageLoading, setIsProfileImageLoading] = useState(false);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [imageLoadStarted, setImageLoadStarted] = useState(false);

  const handleImageLoad = (imageUrl: string) => {
    setLoadedImages((prev) => new Set([...prev, imageUrl]));
    setIsProfileImageLoading(false);
  };

  const handleImageError = (imageUrl: string) => {
    setLoadedImages((prev) => new Set([...prev, imageUrl]));
    setIsProfileImageLoading(false);
  };

  const imageUrl = useMemo(() => {
    if (preloadedImageUrl && preloadedImageUrl !== "null" && preloadedImageUrl !== "") {
      return preloadedImageUrl;
    }
    const logo = providerProfile?.logoOrHeadshot;
    if (logo && logo !== "null" && logo !== "" && logo !== null) {
      return logo;
    }
    return null;
  }, [preloadedImageUrl, providerProfile]);

  // Only start loading image when shouldLoadImage is true (triggered by Intersection Observer)
  useEffect(() => {
    if (!shouldLoadImage || !imageUrl || imageUrl === "null" || imageUrl === "" || imageLoadStarted) {
      return;
    }

    // Mark that we've started loading
    setImageLoadStarted(true);

    // If preloaded, mark as loaded immediately
    if (preloadedImageUrl && preloadedImageUrl === imageUrl) {
      setIsImageLoading(false);
      return;
    }

    // Start loading the image
    setIsImageLoading(true);
    const img = new Image();
    let isHandled = false;
    
    const handleLoad = () => {
      if (!isHandled) {
        setIsImageLoading(false);
        isHandled = true;
      }
    };
    
    const handleError = () => {
      if (!isHandled) {
        setIsImageLoading(false);
        isHandled = true;
      }
    };
    
    img.onload = handleLoad;
    img.onerror = handleError;
    img.src = imageUrl;
    
    // Check if already cached
    if (img.complete && img.naturalWidth > 0) {
      setIsImageLoading(false);
      isHandled = true;
    }

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl, preloadedImageUrl, shouldLoadImage, imageLoadStarted]);

  useEffect(() => {
    setProfileImages([]);
    setActiveImageIndex(0);
    setLoadedImages(new Set());
    setShouldLoadImage(false);
    setIsImageLoading(false);
    setImageLoadStarted(false);
  }, [providerProfile?.provider_id]);

  useEffect(() => {
    setActiveImageIndex(0);
    setLoadedImages(new Set());
  }, [profileImages.length]);

  useEffect(() => {
    if (profileImages.length === 0) return;

    profileImages.forEach((imageUrl) => {
      const img = new Image();
      img.onload = () => {
        setLoadedImages((prev) => new Set([...prev, imageUrl]));
      };
      img.onerror = () => {
        setLoadedImages((prev) => new Set([...prev, imageUrl]));
      };
      img.src = imageUrl;
    });
  }, [profileImages]);

  useEffect(() => {
    if (profileImages.length > 0 && isExpanded) {
      const currentImageUrl = profileImages[activeImageIndex];
      if (currentImageUrl && !loadedImages.has(currentImageUrl)) {
        setIsProfileImageLoading(true);
      } else {
        setIsProfileImageLoading(false);
      }
    }
  }, [activeImageIndex, profileImages, loadedImages, isExpanded]);

  useEffect(() => {
    if (!isExpanded || profileImages.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      setActiveImageIndex((prev) => (prev + 1) % profileImages.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isExpanded, profileImages.length]);

  return {
    imageUrl,
    isImageLoading,
    shouldLoadImage,
    setShouldLoadImage,
    profileImages,
    setProfileImages,
    activeImageIndex,
    setActiveImageIndex,
    isProfileImageLoading,
    setIsImageLoading,
    handleImageLoad,
    handleImageError,
  };
};

